import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../components/Layout/MainLayout';
import applicationService from '../services/applicationService';
import DocumentUpload from '../components/DocumentUpload';
import StatusBadge from '../components/StatusBadge';

const CandidateDocumentsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchApplications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchApplications = async () => {
    setLoading(true);
    try {
      const response = await applicationService.getApplications();
      if (response.success) {
        setApplications(response.applications);
        // Auto-select the first active application
        const active = response.applications.find(
          app => app.currentStage !== 'COMPLETED' && app.currentStage !== 'REJECTED'
        );
        setSelectedApplication(active || response.applications[0]);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="flex flex-col items-center justify-center py-20 px-5">
            <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mb-4"></div>
            <p>Loading...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || applications.length === 0) {
    return (
      <MainLayout>
        <div className="p-6 max-w-7xl mx-auto">
          <div className="flex justify-between items-start mb-8 pb-4 border-b-2 border-gray-200">
            <h1 className="m-0 mb-2 text-gray-800 text-3xl">My Documents</h1>
            <p className="m-0 text-gray-600 text-base">Upload and manage your application documents</p>
          </div>
          <div className="text-center py-20 px-5 bg-white rounded-lg shadow-sm">
            <div className="text-6xl mb-4">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="inline-block">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
              </svg>
            </div>
            <h2 className="text-gray-800 mb-2">No Applications Found</h2>
            <p className="text-gray-600 mb-6">You need to create an application before uploading documents.</p>
            <button 
              className="mt-12 px-4 py-2 border-0 rounded bg-[var(--color-accent-green)] text-white text-[15px] font-semibold cursor-pointer transition-all duration-200 w-auto max-w-fit hover:bg-[#5a6b4d]"
              onClick={() => navigate('/candidate/applications/new')}
            >
              Create Application
            </button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-start mb-8 pb-4 border-b-2 border-gray-200">
          <div>
            <h1 className="m-0 mb-2 text-gray-800 text-3xl">My Documents</h1>
            <p className="m-0 text-gray-600 text-base">Upload and manage your application documents</p>
          </div>
          <button 
            className="mt-12 px-4 py-2 border-0 rounded bg-gray-600 text-white text-[15px] font-semibold cursor-pointer transition-all duration-200 w-auto max-w-fit hover:bg-gray-700"
            onClick={() => navigate('/candidate/applications')}
          >
            ← Back to Applications
          </button>
        </div>

        {applications.length > 1 && (
          <div className="bg-white p-5 rounded-lg shadow-sm mb-6 flex items-center gap-4">
            <label htmlFor="app-select" className="font-semibold text-gray-700 whitespace-nowrap">Select Application:</label>
            <select
              id="app-select"
              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-md text-base bg-white cursor-pointer"
              value={selectedApplication?._id || ''}
              onChange={(e) => {
                const app = applications.find(a => a._id === e.target.value);
                setSelectedApplication(app);
              }}
            >
              {applications.map(app => (
                <option key={app._id} value={app._id}>
                  {app.jobPosting} - {app.currentStage.replace(/_/g, ' ')}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedApplication && (
          <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="m-0 mb-2 text-gray-800 text-2xl">{selectedApplication.jobPosting}</h2>
                <p className="m-0 text-gray-600 text-sm">
                  Applied: {new Date(selectedApplication.appliedAt).toLocaleDateString()}
                </p>
              </div>
              <StatusBadge stage={selectedApplication.currentStage} />
            </div>
            <button
              className="bg-transparent border-0 text-blue-600 cursor-pointer text-[0.95rem] p-0 no-underline transition-colors duration-200 hover:text-blue-800 hover:underline"
              onClick={() => navigate(`/applications/${selectedApplication._id}`)}
            >
              View Full Application Details →
            </button>
          </div>
        )}

        {selectedApplication && (
          <div className="mb-8">
            <DocumentUpload 
              applicationId={selectedApplication._id}
              currentStage={selectedApplication.currentStage}
              onUploadSuccess={() => {
                // Optionally refresh or show success message
              }}
            />
          </div>
        )}

        <div className="bg-gray-50 p-6 rounded-lg border-l-4 border-blue-600">
          <h3 className="m-0 mb-5 text-gray-800 text-xl"> Document Requirements</h3>
          <div className="grid grid-cols-[repeat(auto-fit,minmax(250px,1fr))] gap-4 mb-5">
            <div className="bg-white p-4 rounded-md shadow-sm">
              <h4 className="m-0 mb-3 text-gray-700 text-base">Initial Stage</h4>
              <ul className="list-none p-0 m-0">
                <li className="py-1.5 text-gray-600 text-sm">✓ Passport Copy</li>
                <li className="py-1.5 text-gray-600 text-sm">✓ Passport Photo</li>
                <li className="py-1.5 text-gray-600 text-sm">✓ Educational Certificates</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-md shadow-sm">
              <h4 className="m-0 mb-3 text-gray-700 text-base">Medical Stage</h4>
              <ul className="list-none p-0 m-0">
                <li className="py-1.5 text-gray-600 text-sm">✓ Medical Report</li>
              </ul>
            </div>
            <div className="bg-white p-4 rounded-md shadow-sm">
              <h4 className="m-0 mb-3 text-gray-700 text-base">Visa Stage</h4>
              <ul className="list-none p-0 m-0">
                <li className="py-1.5 text-gray-600 text-sm">✓ Visa Documents</li>
              </ul>
            </div>
          </div>
          <p className="bg-yellow-50 border border-yellow-400 px-4 py-3 rounded-md m-0 text-yellow-800 text-[0.95rem]">
             <strong>Note:</strong> All documents must be verified by admin before you can advance to the next stage.
          </p>
        </div>
      </div>
    </MainLayout>
  );
};

export default CandidateDocumentsPage;
